import React, { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  handleInputChangeWithScroll,
  MESSAGE_TYPES,
} from "../../../utils/editIframe";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import {
  clearValidationErrors,
  executeValidationWithNestedArrays,
  handleInputChangeWithValidation,
  handleMediaSelectWithValidation,
  handleNestedArrayInputChangeWithValidation,
  handlePriceEditPageItemRemoveWithValidation,
  handleResetWithValidation,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Price", path: `/price` },
];

function PriceEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const contentKey = "priceContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({
    page_image: {},
    page_content: "",
    price_items: [],
  });

  // ドラッグ&ドロップ並べ替え処理
  const handlePriceItemReorder = useCallback(
    (reorderedItems) => {
      setPageContent((prev) => {
        const newData = {
          ...prev,
          price_items: reorderedItems,
        };

        clearValidationErrors();

        // iframeに全データを送信
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              allData: newData,
              isFullUpdate: true,
            },
            process.env.REACT_APP_ORIGIN
          );

          // 確認のため、少し遅延してもう一度送信
          setTimeout(() => {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                contentKey,
                allData: newData,
                isFullUpdate: true,
              },
              process.env.REACT_APP_ORIGIN
            );
          }, 100);
        }

        return newData;
      });
    },
    [contentKey]
  );

  // ドラッグ&ドロップフック
  const { getDraggableProps } = useDragAndDrop(
    pageContent.price_items || [],
    handlePriceItemReorder
  );

  // データ取得処理
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_price_content/`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      console.log("data", data);
      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchMediaFiles();
  }, [fetchPageContentData, fetchMediaFiles]);

  // pageContentが更新されたらiframeに初期データを送信
  useEffect(() => {
    if (pageContent && Object.keys(pageContent).length > 0) {
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        setTimeout(() => {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              allData: pageContent,
            },
            process.env.REACT_APP_ORIGIN
          );
        }, 100);
      }
    }
  }, [pageContent]);

  // バリデーションルールを定義
  const validationRules = {
    page_content: { required: true },
  };

  const nestedArrayValidationRules = {
    price_items: {
      firstLevel: {
        section_title: { required: true },
      },
      secondLevel: {
        title: { required: true },
        price: { required: true },
      },
      secondLevelField: "section_items",
    },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    page_content: "料金ページ本文",
  };

  // 更新処理
  const handlePageDataSubmit = async () => {
    // 配列の数をチェック
    if (!pageContent.price_items || pageContent.price_items.length === 0) {
      alert("料金項目は少なくとも1つ必要です");
      return;
    }

    // 空のセクションアイテムをチェック
    for (let i = 0; i < pageContent.price_items.length; i++) {
      const item = pageContent.price_items[i];
      if (!item.section_items || item.section_items.length === 0) {
        alert(`料金セクション${i + 1}には少なくとも1つの料金項目が必要です`);
        return;
      }
    }

    // HTML要素のname属性を動的に更新
    pageContent.price_items.forEach((item, index) => {
      item.section_items.forEach((_, priceIndex) => {
        const titleElement = document.querySelector(
          `input[data-index="${index}-${priceIndex}"][name="title"]`
        );
        if (titleElement) {
          titleElement.setAttribute(
            "name",
            `price_item${index}_${priceIndex}_title`
          );
        }
        const priceElement = document.querySelector(
          `input[data-index="${index}-${priceIndex}"][name="price"]`
        );
        if (priceElement) {
          priceElement.setAttribute(
            "name",
            `price_item${index}_${priceIndex}_price`
          );
        }
      });
    });

    // バリデーション実行
    if (
      !executeValidationWithNestedArrays(
        pageContent,
        validationRules,
        nestedArrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    const formData = { page_content: pageContent };

    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_page_price_content/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "更新に失敗しました");

      setMessage(data.message || "正常に更新されました");
      await fetchPageContentData(); // 最新データ再取得

      // 成功メッセージを3秒後に消す
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      alert(`更新に失敗しました: ${error.message}`);
    }
    setLoadingText("");
  };

  // リセット処理
  const handleReset = () => {
    handleResetWithValidation(fetchPageContentData);
  };

  // 入力変更処理（単一フィールド用）
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const eventType = e.type;

    handleInputChangeWithValidation(e, setPageContent);

    // iframe通信処理
    setPageContent((prev) => {
      const newData = { ...prev, [name]: value };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_${name}`;
        const messageType =
          eventType === "focus"
            ? MESSAGE_TYPES.SCROLL_TO
            : MESSAGE_TYPES.TEXT_UPDATE;

        iframe.contentWindow.postMessage(
          {
            type: messageType,
            target: targetClass,
            contentKey,
            fieldName: name,
            value,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // 繰り返し要素（price_items）の入力変更
  const handlePriceItemInputChange = (e, index, fieldName) => {
    const eventType = e.type;

    handleNestedArrayInputChangeWithValidation(
      e,
      index,
      fieldName,
      setPageContent,
      "price_items",
      null,
      null,
      (newData, index, fieldName, value, eventType) => {
        // iframe通信処理
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          const targetClass = `.edit_price_item${index}_${fieldName}`;
          const messageType =
            eventType === "focus"
              ? MESSAGE_TYPES.SCROLL_TO
              : MESSAGE_TYPES.TEXT_UPDATE;

          iframe.contentWindow.postMessage(
            {
              type: messageType,
              target: targetClass,
              contentKey,
              fieldName,
              index,
              value,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }
    );
  };

  // 繰り返し要素（price_items）の画像変更
  const handlePriceItemImageChange = (index, fieldName, media) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        price_items: prev.price_items.map((item, i) =>
          i === index ? { ...item, [fieldName]: media } : item
        ),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_price_item${index}_${fieldName}`;

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.IMAGE_UPDATE,
            target: targetClass,
            contentKey,
            fieldName,
            index,
            value: media,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // 繰り返し要素（price_items）の追加
  const addPriceItem = (insertAfterIndex = null) => {
    setPageContent((prev) => {
      const newItem = {
        section_title: "",
        section_items: [{ title: "", price: "" }],
      };

      let newPriceItems;
      if (insertAfterIndex !== null) {
        // 指定位置に挿入
        newPriceItems = [
          ...prev.price_items.slice(0, insertAfterIndex + 1),
          newItem,
          ...prev.price_items.slice(insertAfterIndex + 1),
        ];
      } else {
        // 末尾に追加
        newPriceItems = [...prev.price_items, newItem];
      }

      const newData = {
        ...prev,
        price_items: newPriceItems,
      };

      // iframeに全データを送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        setTimeout(() => {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }, 50);
      }

      return newData;
    });
  };

  // 繰り返し要素（price_items）の削除
  const removePriceItem = (index) => {
    if (pageContent.price_items.length <= 1) {
      alert("料金セクションは最低1つ必要です");
      return;
    }

    if (!window.confirm("この料金セクションを削除してもよろしいですか？")) {
      return;
    }

    setPageContent((prev) => {
      const newData = {
        ...prev,
        price_items: prev.price_items.filter((_, i) => i !== index),
      };

      // iframe通信処理
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        setTimeout(() => {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }, 50);
      }

      return newData;
    });

    // 削除後に空文字フィールドを再チェック
    setTimeout(() => {
      setPageContent((currentData) => {
        // HTML要素のname属性を更新
        currentData.price_items.forEach((item, index) => {
          item.section_items.forEach((_, priceIndex) => {
            const titleElement = document.querySelector(
              `input[data-index="${index}-${priceIndex}"][name="title"]`
            );
            const priceElement = document.querySelector(
              `input[data-index="${index}-${priceIndex}"][name="price"]`
            );
            if (titleElement)
              titleElement.setAttribute(
                "name",
                `price_item${index}_${priceIndex}_title`
              );
            if (priceElement)
              priceElement.setAttribute(
                "name",
                `price_item${index}_${priceIndex}_price`
              );
          });
        });

        executeValidationWithNestedArrays(
          currentData,
          validationRules,
          nestedArrayValidationRules,
          fieldLabels,
          () => {}
        );

        return currentData;
      });
    }, 100);
  };

  // メディア選択処理
  const handleMediaSelect = (fieldName, media) => {
    handleMediaSelectWithValidation(fieldName, media, setPageContent);

    // iframe通信処理
    setPageContent((prev) => {
      const newData = {
        ...prev,
        [fieldName]: media,
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_${fieldName}`;

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.IMAGE_UPDATE,
            target: targetClass,
            contentKey,
            fieldName,
            value: media,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // 料金情報の変更ハンドラー
  const handlePriceChange = (itemIndex, priceIndex, field, value) => {
    // 仮のEventオブジェクトを作成
    const fakeEvent = {
      target: {
        value: value,
      },
      type: "change",
    };

    // エラークラスをクリア（対象の要素を探してクリア）
    const element = document.querySelector(
      `input[data-index="${itemIndex}-${priceIndex}"][name="${field}"]`
    );
    if (element) {
      fakeEvent.target = element;
    }

    handleNestedArrayInputChangeWithValidation(
      fakeEvent,
      itemIndex,
      field,
      setPageContent,
      "price_items",
      priceIndex,
      "section_items",
      (newData, itemIndex, field, value, _, priceIndex) => {
        // iframe通信処理
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          const targetClass = `.edit_price_item${itemIndex}_${priceIndex}_${field}`;

          setTimeout(() => {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                target: targetClass,
                contentKey,
                fieldName: field,
                index: itemIndex,
                priceIndex,
                value,
                allData: newData,
              },
              process.env.REACT_APP_ORIGIN
            );

            // スクロール命令を別途送信
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.SCROLL_TO,
                target: targetClass,
                contentKey,
              },
              process.env.REACT_APP_ORIGIN
            );
          }, 50);
        }
      }
    );
  };

  // 料金項目を削除
  const handleRemovePrice = (itemIndex, priceIndex) => {
    const currentItem = pageContent.price_items[itemIndex];
    if (currentItem.section_items.length <= 1) {
      alert("各料金セクションには最低1つの料金項目が必要です");
      return;
    }

    handlePriceEditPageItemRemoveWithValidation(
      itemIndex,
      setPageContent,
      "price_items",
      nestedArrayValidationRules,
      fieldLabels,
      priceIndex,
      "section_items",
      (newData, itemIndex, priceIndex) => {
        // iframe通信処理
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          setTimeout(() => {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                contentKey,
                allData: newData,
              },
              process.env.REACT_APP_ORIGIN
            );

            // 削除メッセージも送信
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.DELETE_PRICE_ITEM,
                contentKey,
                itemIndex,
                priceIndex,
                allData: newData,
              },
              process.env.REACT_APP_ORIGIN
            );
          }, 50);
        }
      }
    );
  };

  // 料金項目を追加
  const handleAddPrice = (itemIndex) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        price_items: prev.price_items.map((item, index) => {
          if (index === itemIndex) {
            const updatedSectionItems = [...(item.section_items || [])];
            updatedSectionItems.push({ title: "", price: "" });

            return {
              ...item,
              section_items: updatedSectionItems,
            };
          }
          return item;
        }),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        setTimeout(() => {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );

          // 追加メッセージも送信
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.ADD_PRICE_ITEM,
              contentKey,
              itemIndex,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }, 50);
      }

      return newData;
    });
  };

  // ファイルドロップ処理
  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setLoadingText("アップロード中");
      // ドラッグ&ドロップまたはファイル選択の両方に対応
      const files = e.dataTransfer?.files || e.target?.files;
      const file = files?.[0];

      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        await fetchMediaFiles();
        if (data.images?.[0]) {
          selectMedia(data.images[0]);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
        alert("アップロードに失敗しました");
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia]
  );

  // スタイル要素を作成
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      .loop {
        display: grid !important;
        grid-template-columns: auto !important;
      }
    `;

    // head に追加
    document.head.appendChild(styleElement);

    // クリーンアップ関数
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    console.log("pageContent", pageContent);
  }, [pageContent]);

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />

            <div className="form">
              {/* ページ上部 */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <textarea
                        className="input2"
                        name="page_content"
                        value={pageContent.page_content}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="ページ本文"
                        data-required="true"
                      />
                      <div className="input3 img-form">
                        <MediaSelector
                          label="ページ画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="page_image"
                          selectedMedia={pageContent.page_image}
                          onSelect={(media) =>
                            handleMediaSelect("page_image", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 繰り返し */}
              <section>
                <div className="area2">
                  {pageContent.price_items?.map((price_item, index) => (
                    <div
                      className="item"
                      key={index}
                      {...getDraggableProps(index)}
                    >
                      <div className="ctrl">
                        <p>{index + 1}</p>
                        <div className="icon-wrap">
                          <i
                            className="icon delete"
                            onClick={() => removePriceItem(index)}
                          ></i>
                          <i
                            className="icon add"
                            onClick={() => addPriceItem(index)}
                          ></i>
                        </div>
                      </div>
                      <div className="forms loop">
                        <input
                          type="text"
                          name={`price_item${index}_section_title`}
                          value={price_item.section_title}
                          onChange={(e) =>
                            handlePriceItemInputChange(
                              e,
                              index,
                              "section_title"
                            )
                          }
                          onFocus={(e) =>
                            handlePriceItemInputChange(
                              e,
                              index,
                              "section_title"
                            )
                          }
                          placeholder="タイトル"
                        />
                        <dl className="prices txt-form">
                          {price_item.section_items?.map((price, i) => (
                            <React.Fragment key={i}>
                              <dt>
                                <input
                                  type="text"
                                  name="title"
                                  value={price.title || ""}
                                  placeholder="料金区分を入力"
                                  onChange={(e) =>
                                    handlePriceChange(
                                      index,
                                      i,
                                      "title",
                                      e.target.value
                                    )
                                  }
                                  onFocus={(e) =>
                                    handlePriceChange(
                                      index,
                                      i,
                                      "title",
                                      e.target.value
                                    )
                                  }
                                  data-label={`料金区分 ${i + 1}`}
                                  data-index={`${index}-${i}`}
                                  data-required="true"
                                />
                              </dt>
                              <dd
                                onClick={(e) => {
                                  // input要素のクリックでは削除しない
                                  if (e.target.tagName !== "INPUT") {
                                    handleRemovePrice(index, i);
                                  }
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <input
                                  type="text"
                                  name="price"
                                  value={price.price || ""}
                                  placeholder="金額を入力"
                                  onChange={(e) =>
                                    handlePriceChange(
                                      index,
                                      i,
                                      "price",
                                      e.target.value
                                    )
                                  }
                                  onFocus={(e) =>
                                    handlePriceChange(
                                      index,
                                      i,
                                      "price",
                                      e.target.value
                                    )
                                  }
                                  data-label={`金額 ${i + 1}`}
                                  data-index={`${index}-${i}`}
                                  data-required="true"
                                />
                              </dd>
                            </React.Fragment>
                          ))}
                        </dl>

                        <div className="accent-wrap">
                          <p
                            onClick={() => handleAddPrice(index)}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="icon add"></i>
                            <span>Price</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>

            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe path="/price" data={pageContent} contentKey={contentKey} />
    </>
  );
}

export default function PriceEditPage() {
  return (
    <MediaProvider>
      <PriceEditPageContent />
    </MediaProvider>
  );
}
